import { type ReactElement, createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useStores } from 'src/components/common/root-store-provider/root-store-provider';
import { isDevelopment, noop } from 'src/utils';
import { useFacebookEvent } from '@features/Analytics/hooks/mutation/useFacebookEvent';
import { usePinterestEvent } from '@features/Analytics/hooks/mutation/usePinterestEvent';
import { useSnapchatEvent } from '@features/Analytics/hooks/mutation/useSnapchatEvent';
import { useTikTokEvent } from '@features/Analytics/hooks/mutation/useTikTokEvent';
import { useAppLovinEvent } from '@features/Analytics/hooks/useAppLovinEvent';
import { useGoogleEvent } from '@features/Analytics/hooks/useGoogleEvent';
import type { TpAppLovinParameters, TpFacebookParameters, TpGoogleParameters, TpPinterestParameters, TpSnapchatParameters, TpTikTokParameters } from '@features/Analytics/types';
import { createAppLovinUserData } from '@features/Analytics/utils';
import { type BingEventPayload, useBingEvent } from '@features/Analytics/hooks/useBingEvent';
import { useShouldInit } from './useShouldInit';
const AnalyticsDebuggerContext = createContext<{
  events: any;
  logEvent: any;
  clearEvents: () => void;
  eventsNumberVisible: boolean;
  setEventsNumberVisible: (value: boolean) => void;
}>({
  events: [],
  logEvent: () => {},
  clearEvents: () => {},
  eventsNumberVisible: false,
  setEventsNumberVisible: noop
});
export const useAnalyticsDebugger = () => useContext(AnalyticsDebuggerContext);
export const useAnalytics = () => {
  const {
    logEvent
  } = useAnalyticsDebugger();
  const {
    authStore
  } = useStores();
  const user = authStore?.user;
  const {
    shouldPixelInit,
    shouldGoogleAnalyticsInit,
    shouldPTInit,
    shouldBingInit,
    shouldSnapchatInit,
    shouldTikTokInit,
    shouldAppLovinInit
  } = useShouldInit();
  const {
    clientEvent: pinterestEvent
  } = usePinterestEvent();
  const {
    clientEvent: googleEvent
  } = useGoogleEvent();
  const {
    clientEvent: bingEvent
  } = useBingEvent();
  const {
    clientEvent: facebookEvent
  } = useFacebookEvent();
  const {
    clientEvent: snapchatEvent
  } = useSnapchatEvent();
  const {
    clientEvent: tikTokClientEvent
  } = useTikTokEvent();
  const {
    clientEvent: appLovinEvent
  } = useAppLovinEvent();
  const trackFacebookEvent = useCallback((value: TpFacebookParameters) => {
    shouldPixelInit && facebookEvent(value);
    isDevelopment && logEvent({
      name: value.eventName,
      parameters: value.options,
      source: 'FB'
    });
  }, [shouldPixelInit, facebookEvent, logEvent]);
  const trackPinterestEvent = useCallback((value: Omit<TpPinterestParameters, 'clickId' | 'externalId'>) => {
    const modifiedValue = Object.assign(value, {
      options: {
        email: value?.options?.email ? value?.options?.email : user?.email ?? '',
        ...value.options
      }
    });
    shouldPTInit && pinterestEvent(modifiedValue);
    isDevelopment && logEvent({
      name: value.eventName,
      parameters: value.options,
      source: 'PT'
    });
  }, [shouldPTInit, pinterestEvent, logEvent, user?.email]);
  const trackBingEvent = useCallback((value: BingEventPayload) => {
    shouldBingInit && bingEvent(value);
    isDevelopment && logEvent({
      name: value.eventName,
      parameters: value.options,
      source: 'bing'
    });
  }, [bingEvent, logEvent, shouldBingInit]);
  const trackGoogleEvent = useCallback((value: TpGoogleParameters) => {
    const modifiedValue = Object.assign(value, {
      options: {
        email: value?.options?.email ? value?.options?.email : user?.email,
        ...(user?.id && {
          mentalId: user?.id
        }),
        ...value.options
      }
    });
    shouldGoogleAnalyticsInit && googleEvent(modifiedValue);
    isDevelopment && logEvent({
      name: modifiedValue.eventName,
      parameters: modifiedValue.options,
      source: 'GA'
    });
  }, [googleEvent, logEvent, shouldGoogleAnalyticsInit, user?.email, user?.id]);
  const trackSnapchatEvent = useCallback((value: TpSnapchatParameters) => {
    shouldSnapchatInit && snapchatEvent(value);
    isDevelopment && logEvent({
      name: value.eventName,
      parameters: value.options,
      source: 'SNAP'
    });
  }, [shouldSnapchatInit, snapchatEvent, logEvent]);
  const trackTikTokEvent = useCallback((value: TpTikTokParameters) => {
    shouldTikTokInit && tikTokClientEvent(value);
    isDevelopment && logEvent({
      name: value.eventName,
      parameters: value.options,
      source: 'TikTok'
    });
  }, [shouldTikTokInit, tikTokClientEvent, logEvent]);
  const trackAppLovin = useCallback((value: TpAppLovinParameters) => {
    const userData = createAppLovinUserData({
      userEmail: user?.email,
      userId: user?.id
    });
    const modifiedValue = userData ? {
      ...value,
      options: {
        ...value.options,
        user_data: {
          ...userData,
          ...value.options?.user_data
        }
      }
    } : value;
    shouldAppLovinInit && appLovinEvent(modifiedValue);
    isDevelopment && logEvent({
      name: modifiedValue.eventName,
      parameters: modifiedValue.options,
      source: 'AppLovin'
    });
  }, [shouldAppLovinInit, appLovinEvent, logEvent, user?.email, user?.id]);
  return {
    trackFacebookEvent,
    trackGoogleEvent,
    trackPinterestEvent,
    trackBingEvent,
    trackSnapchatEvent,
    trackTikTokEvent,
    trackAppLovin
  };
};
export const AnalyticsDebuggerProvider = isDevelopment ? ({
  children
}: {
  children: ReactElement;
}) => {
  const [events, setEvents] = useState<{
    [key: string]: any;
  }[]>([]);
  const [eventsNumberVisible, setEventsNumberVisible] = useState(false);
  const logEvent = useCallback(({
    name,
    parameters,
    source
  }: {
    name: string;
    parameters: Record<string, any>;
    source: string;
  }) => {
    setEvents(events => [...events, {
      name,
      params: parameters,
      date: new Date(),
      source
    }]);
  }, []);
  const clearEvents = useCallback(() => {
    setEvents([]);
  }, []);
  const value = useMemo(() => ({
    events,
    logEvent,
    clearEvents,
    eventsNumberVisible,
    setEventsNumberVisible
  }), [events, logEvent, clearEvents, eventsNumberVisible]);
  return <AnalyticsDebuggerContext.Provider value={value}>
          {children}
        </AnalyticsDebuggerContext.Provider>;
} : ({
  children
}: {
  children: ReactElement;
}) => children;